import { notifications } from '@utils/notifications/index';
import { getLogger } from '@utils/general';

const logger = getLogger('src.utils.react.hooks.useFormSubmit');

/**
 * Returns an async submit handler that branches between create and update,
 * shows success/error notifications, and calls handleClose on success.
 *
 * @param {object} options
 * @param {Function} options.saveCreator   - (payload) => { mutation, execute }
 * @param {Function} options.updateCreator - (id, payload) => { mutation, execute }
 * @param {string|null} options.existingId - id of the record being edited, or null/undefined for create
 * @param {Function} options.addReturnProperties - (mutation) => void — register return props
 * @param {object}   options.logMessages   - { success: string, fail: string } for create
 * @param {object}   [options.updateLogMessages] - { success: string, fail: string } for update
 *                                                  (falls back to logMessages)
 * @param {Function} [options.onSuccess]   - (data) => void  — called with response data on success
 * @param {Function} options.handleClose   - called after a successful save
 * @returns {Function} async () => unknown
 */
export function useFormSubmit({
  saveCreator,
  updateCreator,
  existingId,
  addReturnProperties,
  logMessages,
  updateLogMessages,
  onSuccess,
  handleClose,
}) {
  return async (payload) => {
    const isNew = !existingId;
    const messages = isNew ? logMessages : (updateLogMessages ?? logMessages);

    const { mutation, execute } = isNew
      ? saveCreator(payload)
      : updateCreator(existingId, payload);

    if (addReturnProperties) {
      addReturnProperties(mutation);
    }

    let result;
    await execute()
      .then(({ error, data }) => {
        if (data) {
          result = data;
          if (typeof onSuccess === 'function') {
            onSuccess(data);
          }
          notifications.success(messages.success);
          handleClose();
        }
        if (error) {
          notifications.error(messages.fail);
          logger.error(error, messages.fail);
        }
      })
      .catch((error) => {
        notifications.error(messages.fail);
        logger.error(error, messages.fail);
      });

    return result;
  };
}
